import { useEffect, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { cn } from "@/lib/utils";

interface PdfThumbnailsProps {
  pdf: PDFDocumentProxy;
  numPages: number;
  currentPage: number;
  onPageSelect: (page: number) => void;
  darkMode: boolean;
}

interface ThumbnailEntry {
  page: number;
  url: string;
}

export function PdfThumbnails({
  pdf,
  numPages,
  currentPage,
  onPageSelect,
  darkMode,
}: PdfThumbnailsProps) {
  const [thumbnails, setThumbnails] = useState<ThumbnailEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    const blobUrls: string[] = [];

    async function generateThumbnails() {
      const list: ThumbnailEntry[] = [];

      for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
        if (cancelled) break;

        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 0.2 });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d")!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport,
        }).promise;

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/png")
        );

        if (!blob || cancelled) continue;

        const url = URL.createObjectURL(blob);
        blobUrls.push(url);
        list.push({ page: pageNumber, url });
      }

      if (!cancelled) {
        setThumbnails(list);
      }
    }

    generateThumbnails();

    return () => {
      cancelled = true;
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [pdf, numPages]);

  return (
    <div className={cn(
      "w-32 shrink-0 border-r border-border overflow-y-auto scrollbar-hide transition-colors duration-300",
      darkMode ? "bg-neutral-800" : "bg-muted/30"
    )}>
      <div className="flex flex-col items-center gap-3 py-3">
        {thumbnails.length === 0
          ? Array.from({ length: numPages }).map((_, i) => (
              <div
                key={i + 1}
                className={cn(
                  "w-[90px] aspect-[3/4] rounded-md animate-pulse",
                  darkMode ? "bg-neutral-700" : "bg-muted"
                )}
              />
            ))
          : thumbnails.map((thumb) => (
              <button
                key={thumb.page}
                onClick={() => onPageSelect(thumb.page)}
                className={cn(
                  "w-[90px] rounded-md overflow-hidden border transition-all",
                  darkMode ? "pdf-thumbnail-dark" : "pdf-thumbnail-light",
                  currentPage === thumb.page
                    ? "border-primary shadow-sm ring-1 ring-primary"
                    : "border-transparent hover:border-border"
                )}
              >
                <img
                  src={thumb.url}
                  alt={`Page ${thumb.page}`}
                  className="w-full h-auto block"
                />
                <div className={cn(
                  "text-xs text-center py-1",
                  darkMode ? "text-neutral-400" : "text-muted-foreground"
                )}>
                  {thumb.page}
                </div>
              </button>
            ))}
      </div>
    </div>
  );
}
